"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  type AnalyticsConsent,
  initializeAnalytics,
  readAnalyticsConsent,
  trackPageView,
  updateAnalyticsConsent,
} from "@/lib/analytics";
import {
  type MarketingConsent,
  initializeMetaPixel,
  metaPageView,
  metaPixelEnabled,
  needsConsentRefresh,
  readMarketingConsent,
  updateMarketingConsent,
} from "@/lib/meta-pixel";
import { statsConsent, statsPageView } from "@/lib/stats";

type AnalyticsContextValue = {
  consent: AnalyticsConsent;
  /** Meta Pixel / advertising consent: independent from `consent` (analytics). */
  marketingConsent: MarketingConsent;
  openPreferences: () => void;
};

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function useAnalytics() {
  const value = useContext(AnalyticsContext);
  if (!value)
    throw new Error("useAnalytics must be used inside <AnalyticsProvider>");
  return value;
}

function ConsentPanel({
  detailed,
  analyticsEnabled,
  marketingEnabled,
  refresh,
  onAnalyticsChange,
  onMarketingChange,
  onAccept,
  onReject,
  onShowDetails,
  onSave,
}: {
  detailed: boolean;
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
  /** An older banner was answered: the first layer says the privacy options were updated. */
  refresh: boolean;
  onAnalyticsChange: (enabled: boolean) => void;
  onMarketingChange: (enabled: boolean) => void;
  onAccept: () => void;
  onReject: () => void;
  onShowDetails: () => void;
  onSave: () => void;
}) {
  const t = useTranslations("cookieConsent");

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-h-[calc(100dvh-1.5rem)] max-w-[760px] overflow-y-auto border border-[rgba(242,235,224,0.22)] bg-[rgba(11,9,7,0.98)] p-3.5 shadow-2xl min-[360px]:p-4 sm:inset-x-6 sm:bottom-6 sm:p-6"
    >
      <p
        id="cookie-consent-title"
        className="display text-[17px] leading-tight min-[360px]:text-[19px] sm:text-[max(1.7rem,21px)]"
      >
        {detailed ? t("preferencesTitle") : t("title")}
      </p>

      {!detailed ? (
        <>
          <p className="body-copy mt-1.5 max-w-[650px] text-[12.5px] leading-[1.45] text-[var(--smoke)] min-[360px]:mt-2 min-[360px]:text-[13px] min-[360px]:leading-[1.5] sm:mt-3 sm:text-[max(0.94rem,13px)]">
            {refresh ? t("marketingUpdate") : metaPixelEnabled ? t("textMarketing") : t("text")}{" "}
            <Link
              href="/cookies"
              className="underline decoration-[var(--copper)] underline-offset-4 hover:text-[var(--cream)]"
            >
              {t("policy")}
            </Link>
          </p>
          {/* Accept and Reject are equal in size; Configure is quieter but a full-size touch target */}
          <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-5 sm:grid-cols-[1fr_1fr_auto] sm:gap-2.5">
            <button
              type="button"
              onClick={onAccept}
              className="btn btn-solid min-h-[44px] w-full !px-2"
            >
              {metaPixelEnabled ? t("acceptAll") : t("accept")}
            </button>
            <button
              type="button"
              onClick={onReject}
              className="btn min-h-[44px] w-full border border-[var(--line)] !px-2"
            >
              {t("reject")}
            </button>
            <button
              type="button"
              onClick={onShowDetails}
              className="label-mono col-span-2 min-h-[44px] px-4 text-[var(--smoke)] underline decoration-[var(--copper)] underline-offset-4 hover:text-[var(--cream)] sm:col-span-1"
            >
              {t("settings")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="mt-5 divide-y divide-[var(--line)] border-y border-[var(--line)]">
            <div className="flex items-start justify-between gap-5 py-4">
              <div>
                <p className="label-mono text-[var(--cream)]">
                  {t("necessaryTitle")}
                </p>
                <p className="body-copy mt-1.5 text-[max(0.88rem,12.5px)] text-[var(--smoke)]">
                  {t("necessaryDescription")}
                </p>
              </div>
              <span className="label-mono shrink-0 text-copper">
                {t("alwaysActive")}
              </span>
            </div>
            <label className="flex cursor-pointer items-start justify-between gap-5 py-4">
              <span>
                <span className="label-mono block text-[var(--cream)]">
                  {t("analyticsTitle")}
                </span>
                <span className="body-copy mt-1.5 block text-[max(0.88rem,12.5px)] text-[var(--smoke)]">
                  {t("analyticsDescription")}
                </span>
              </span>
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(event) => onAnalyticsChange(event.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 accent-[var(--copper)]"
              />
            </label>
            {metaPixelEnabled && (
              <label className="flex cursor-pointer items-start justify-between gap-5 py-4">
                <span>
                  <span className="label-mono block text-[var(--cream)]">
                    {t("marketingTitle")}
                  </span>
                  <span className="body-copy mt-1.5 block text-[max(0.88rem,12.5px)] text-[var(--smoke)]">
                    {t("marketingDescription")}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={marketingEnabled}
                  onChange={(event) => onMarketingChange(event.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 accent-[var(--copper)]"
                />
              </label>
            )}
          </div>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <button
              type="button"
              onClick={onSave}
              className="btn btn-solid min-h-12 w-full"
            >
              {t("save")}
            </button>
            <Link
              href="/cookies"
              className="btn min-h-12 w-full border border-[var(--line)]"
            >
              {t("policy")}
            </Link>
          </div>
        </>
      )}
    </section>
  );
}

export default function AnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [consent, setConsent] = useState<AnalyticsConsent>(null);
  const [ready, setReady] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState<MarketingConsent>(null);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const lastMetaPage = useRef<string | null>(null);
  const lastPage = useRef<string | null>(null);
  const lastStatsPage = useRef<string | null>(null);

  useEffect(() => {
    const saved = readAnalyticsConsent();
    const savedMarketing = metaPixelEnabled ? readMarketingConsent() : null;
    setConsent(saved);
    setAnalyticsEnabled(saved === "granted");
    setMarketingConsent(savedMarketing);
    setMarketingEnabled(savedMarketing === "granted");
    // Answered an older banner: their analytics choice stays in force until they answer; they get the
    // same simple first layer (Accept all / Reject / Configure) with a short "options updated" line, once
    const ask = needsConsentRefresh(saved);
    setRefresh(ask);
    setPanelOpen(saved === null || ask);
    setReady(true);
    if (saved === "granted") initializeAnalytics();
    if (savedMarketing === "granted") initializeMetaPixel();
  }, []);

  // Own aggregated stats: every page view (path only), independent of GA4 consent
  useEffect(() => {
    if (!ready || lastStatsPage.current === pathname) return;
    lastStatsPage.current = pathname;
    statsPageView(pathname);
  }, [pathname, ready]);

  useEffect(() => {
    if (!ready || consent !== "granted") return;
    const page = `${pathname}${window.location.search}`;
    if (lastPage.current === page) return;
    lastPage.current = page;
    const frame = window.requestAnimationFrame(trackPageView);
    return () => window.cancelAnimationFrame(frame);
  }, [consent, pathname, ready]);

  // Meta Pixel PageView: its own consent and its own "last page", so a late grant still reports this
  // page. The page is marked when the frame runs (not when it is scheduled), which stays correct under
  // React strict mode's double effect and never sends the same page twice.
  useEffect(() => {
    if (!ready || marketingConsent !== "granted") return;
    const page = `${pathname}${window.location.search}`;
    const frame = window.requestAnimationFrame(() => {
      if (lastMetaPage.current === page) return;
      lastMetaPage.current = page;
      metaPageView();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [marketingConsent, pathname, ready]);

  const decide = useCallback((next: Exclude<AnalyticsConsent, null>, nextMarketing?: Exclude<MarketingConsent, null>) => {
    updateAnalyticsConsent(next);
    setConsent(next);
    setAnalyticsEnabled(next === "granted");
    if (metaPixelEnabled && nextMarketing) {
      updateMarketingConsent(nextMarketing);
      statsConsent(nextMarketing, "m"); // own aggregated stats: counts the decision only
      setMarketingConsent(nextMarketing);
      setMarketingEnabled(nextMarketing === "granted");
    }
    setPanelOpen(false);
    setDetailed(false);
    setRefresh(false);
  }, []);

  const openPreferences = useCallback(() => {
    setAnalyticsEnabled(readAnalyticsConsent() === "granted");
    setMarketingEnabled(readMarketingConsent() === "granted");
    setRefresh(false);
    setDetailed(true);
    setPanelOpen(true);
  }, []);

  return (
    <AnalyticsContext.Provider value={{ consent, marketingConsent, openPreferences }}>
      {children}
      {ready && panelOpen && (
        <ConsentPanel
          detailed={detailed}
          analyticsEnabled={analyticsEnabled}
          marketingEnabled={marketingEnabled}
          refresh={refresh}
          onAnalyticsChange={setAnalyticsEnabled}
          onMarketingChange={setMarketingEnabled}
          onAccept={() => decide("granted", "granted")}
          onReject={() => decide("denied", "denied")}
          onShowDetails={() => setDetailed(true)}
          onSave={() =>
            decide(
              analyticsEnabled ? "granted" : "denied",
              marketingEnabled ? "granted" : "denied",
            )
          }
        />
      )}
    </AnalyticsContext.Provider>
  );
}

export function CookieSettingsButton({ className }: { className?: string }) {
  const t = useTranslations("cookieConsent");
  const { openPreferences } = useAnalytics();
  return (
    <button type="button" onClick={openPreferences} className={className}>
      {t("reopen")}
    </button>
  );
}
